import { useQuery } from '@apollo/client';
import { AddCircle, Tv } from '@mui/icons-material';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { ButtonLink } from '~/components/link';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { TvBrandsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NetworkTvBrandsDocument } from '../queries/list.generated';
import { Table } from './components';

export const TVBrandList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const { orderBy } = useOrderByParams<TvBrandsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const { data, loading } = useQuery(NetworkTvBrandsDocument, {
    variables: { networkId: currentNetwork.id, page, perPage, search, ...(orderBy && { orderBy }) },
    onCompleted: (data) => handleOutOfBounds(data.network?.tvBrands.pageInfo),
  });

  const tvBrands = data?.network?.tvBrands.nodes ?? [];
  const totalCount = useTotalCount(data?.network?.tvBrands.pageInfo.nodesCount);

  return (
    <>
      <Toolbar
        actions={
          <ButtonLink color="primary" startIcon={<AddCircle />} variant="contained" to="new">
            {t('pages.tv_brands.actions.new_tv_brand')}
          </ButtonLink>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<Tv />}
        titleText={t('pages.tv_brands.title')}
      />
      <ListPageContainer>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <SearchBar placeholder={t('pages.tv_brands.search_placeholder')} search={search} />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Count
                  selectedCount={0}
                  totalCount={totalCount}
                  thing="common.pluralize.tv_brand_plural"
                />
              </Box>
            </ListActions>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table tvBrands={tvBrands} loading={loading} />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
